/**
 * Save and Reload Data Actor
 *
 * XState actor that saves the current import data and then refetches
 * fresh data from the backend. Used by the "saving" state to persist
 * changes while staying on the current step.
 *
 * Flow:
 * 1. Convert ImportData to API format (toRowsJSON)
 * 2. Save to backend via updateImportPreview
 * 3. Fetch fresh data from backend
 * 4. Convert API format back to ImportData (fromRowsJSON)
 * 5. Return the refreshed data
 */
import { fromPromise } from 'xstate'
import { fetchImportPreviewData, updateImportPreview } from '../../importer-api'
import { ImportData, ImportDataRowsFormat } from '../../importer-types'

export interface SaveAndReloadDataInput {
  importNumber: number
  data: ImportData
  skipMatching: boolean
}

export interface SaveAndReloadDataOutput {
  data: ImportData | null
  skipMatching: boolean
}

/**
 * Convert ImportData (UI format) to ImportDataRowsFormat (API format)
 */
function toRowsJSON(data: ImportData): ImportDataRowsFormat {
  if (!data.rows.length) {
    return {
      headers: [],
      rows: {
        columns: [],
        rows: [],
      },
    }
  }
  const columns = Object.keys(data.rows[0])
  const rows = data.rows.map((obj) => columns.map((key) => obj[key]))
  return {
    headers: data.headers,
    rows: {
      columns,
      rows,
    },
  }
}

/**
 * Convert ImportDataRowsFormat (API format) to ImportData (UI format)
 */
function fromRowsJSON(data: ImportDataRowsFormat): ImportData {
  return {
    headers: data.headers.map((h) => ({
      original: h.original,
      mapped: h.mapped,
    })),
    rows:
      data.rows?.rows?.map(
        (row) =>
          Object.fromEntries(
            (data.rows?.columns || []).map((key, i) => [key, row[i]])
          ) as ImportData['rows'][number]
      ) || [],
  }
}

export const saveAndReloadDataActor = fromPromise<
  SaveAndReloadDataOutput,
  SaveAndReloadDataInput
>(async ({ input }) => {
  // 1. Convert and save to backend (skip_matching is only set on creation, never updated)
  const jsonData = toRowsJSON(input.data)
  await updateImportPreview(input.importNumber, {
    import_data: { data: jsonData },
  })

  // 2. Fetch fresh data from backend
  const response = await fetchImportPreviewData(input.importNumber)

  // 3. Return the refreshed data
  return {
    data: response.data ? fromRowsJSON(response.data) : null,
    skipMatching: response.skip_matching ?? false,
  }
})
